import { Box, styled } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  SText,
  SGallery,
  SAccordion,
  SegmentedSwitch,
  SRoundedContainer
} from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';
import { distances } from '@setvi/shared/styles';
import {
  AnalyticsModules,
  SalesEnablementActionType,
  useAnalytics
} from '@setvi/shared/hooks';

import { AILoader } from 'pages/products/components';
import { loadingSalesEnablementMessages } from 'pages/products/components/ai/loader/messages';

import { AccordionsContainer, AIOverviewContent, Container } from './styled';
import {
  ProductDetailsAccordion,
  productDetailsAccordions,
  ProductDetailsTab,
  useProductInfo
} from './hook';
import {
  NoSalesEnablement,
  SalesKitFeedback
} from '../sales-enablement/components';

export const ContentTitleContainer = styled(Box)(({ theme }) => ({
  marginTop: 12,
  marginBottom: distances.px.base,
  marginLeft: theme.spacing(2)
}));

export interface ProductInfoProps {
  product: Product;
}

export const ProductInfo = ({ product }: ProductInfoProps) => {
  const flags = useFlags();

  const {
    tab,
    setTab,
    salesKit,
    expandedAccordion,
    setExpandedAccordion,
    selectedImage,
    setSelectedImage,
    salesKitLoading,
    salesKitData,
    options,
    images
  } = useProductInfo({ product });
  const { captureAnalytics } = useAnalytics();

  return (
    <>
      {selectedImage && (
        <SGallery
          fullScreen
          images={images}
          errorImage="/images/product-placeholder.svg"
          defaultOpenedImage={selectedImage}
          opened={!!selectedImage}
          onClose={() => setSelectedImage('')}
        />
      )}

      <Container>
        <SRoundedContainer minWidth={352} width="30%">
          <ContentTitleContainer>
            <SText title="Product Photos" weight="semibold" />
          </ContentTitleContainer>
          <SGallery
            onImageClick={img => {
              product.images.length > 0 && setSelectedImage(img);
            }}
            images={images}
            errorImage="/images/product-placeholder.svg"
          />
        </SRoundedContainer>

        <AccordionsContainer>
          {flags?.salesEnablement && !!salesKitData && (
            <SegmentedSwitch<number>
              value={tab}
              setValue={(val: ProductDetailsTab) => {
                if (val === ProductDetailsTab.AIDETAILS) {
                  captureAnalytics(
                    {
                      productSKU: product.sku,
                      item: 'AI Overview',
                      content: salesKitData?.[0]?.content || '-',
                      actionType: SalesEnablementActionType.ItemClick
                    },
                    AnalyticsModules.SalesEnablement
                  );
                }

                setTab(val);
                setExpandedAccordion(
                  val === ProductDetailsTab.DETAILS
                    ? productDetailsAccordions.map(({ title }) => title)
                    : ['AI Overview'] // Only expand AI Overview if it exists
                );
              }}
              options={options}
            />
          )}

          {/* Render regular description */}
          {tab === ProductDetailsTab.DETAILS &&
            productDetailsAccordions.map(
              ({ title, content }: ProductDetailsAccordion) => (
                <SRoundedContainer style={{ padding: '0 16px' }}>
                  <SAccordion
                    key={title}
                    title={title}
                    content={content(product)}
                    expanded={expandedAccordion.includes(title)}
                    onClick={val =>
                      setExpandedAccordion(prev => {
                        const isExpanded = prev.includes(title);
                        return isExpanded
                          ? prev.filter(item => item !== title)
                          : [...prev, val];
                      })
                    }
                  />
                </SRoundedContainer>
              )
            )}

          {/* Render AI Overview */}
          {tab === ProductDetailsTab.AIDETAILS && flags?.salesEnablement && (
            <SRoundedContainer style={{ padding: '0 16px' }}>
              <SAccordion
                key="AI Overview"
                title="AI Overview"
                content={
                  salesKitLoading ? (
                    <AIOverviewContent>
                      <AILoader messages={loadingSalesEnablementMessages} />
                    </AIOverviewContent>
                  ) : (
                    <>
                      {salesKitData?.[0]?.content ? (
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: salesKitData?.[0]?.content || '-'
                          }}
                        />
                      ) : (
                        <AIOverviewContent>
                          <NoSalesEnablement
                            disabled={salesKit?.disabled || false}
                            metadataError={false}
                          />
                        </AIOverviewContent>
                      )}
                    </>
                  )
                }
                rightSection={
                  <SalesKitFeedback
                    product={product}
                    label="How can we improve AI Overview?"
                  />
                }
                expanded={expandedAccordion.includes('AI Overview')}
                onClick={val =>
                  setExpandedAccordion(prev => {
                    const isExpanded = prev.includes('AI Overview');
                    return isExpanded
                      ? prev.filter(item => item !== 'AI Overview')
                      : [...prev, val];
                  })
                }
              />
            </SRoundedContainer>
          )}
        </AccordionsContainer>
      </Container>
    </>
  );
};
